import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import { 
  Search, Filter, Calendar, Clock, User, Building2, FileText, 
  ChevronDown, ChevronLeft, ChevronRight, RefreshCw, Download,
  Activity, TrendingUp, Users, Shield, X, Check
} from 'lucide-react';

// Action type labels and colors
const actionConfig = {
  // Authentication actions
  login: { label: 'User Login', color: 'bg-purple-100 text-purple-800', icon: Shield },
  login_failed: { label: 'Failed Login', color: 'bg-red-100 text-red-800', icon: Shield },
  logout: { label: 'User Logout', color: 'bg-neutral-100 text-[#1a1a2e]', icon: Shield },
  register: { label: 'User Registered', color: 'bg-green-100 text-green-800', icon: Users },
  change_password: { label: 'Password Changed', color: 'bg-yellow-100 text-yellow-800', icon: Shield },
  change_password_failed: { label: 'Password Change Failed', color: 'bg-red-100 text-red-800', icon: Shield },
  
  // Employee actions
  create_employee: { label: 'Created Employee', color: 'bg-green-100 text-green-800', icon: Users },
  update_employee: { label: 'Updated Employee', color: 'bg-blue-100 text-blue-800', icon: Users },
  delete_employee: { label: 'Deleted Employee', color: 'bg-red-100 text-red-800', icon: Users },
  update_profile: { label: 'Profile Updated', color: 'bg-blue-100 text-blue-800', icon: Users },
  complete_profile: { label: 'Profile Completed', color: 'bg-green-100 text-green-800', icon: Users },
  
  // Client actions
  create_client: { label: 'Created Client', color: 'bg-green-100 text-green-800', icon: Building2 },
  update_client: { label: 'Updated Client', color: 'bg-blue-100 text-blue-800', icon: Building2 },
  delete_client: { label: 'Deleted Client', color: 'bg-red-100 text-red-800', icon: Building2 },
  
  // Task actions
  create_task: { label: 'Created Task', color: 'bg-green-100 text-green-800', icon: FileText },
  update_task: { label: 'Updated Task', color: 'bg-blue-100 text-blue-800', icon: FileText },
  delete_task: { label: 'Deleted Task', color: 'bg-red-100 text-red-800', icon: FileText },
  
  // Ticket actions
  create_ticket: { label: 'Created Ticket', color: 'bg-green-100 text-green-800', icon: FileText },
  update_ticket: { label: 'Updated Ticket', color: 'bg-blue-100 text-blue-800', icon: FileText },
  
  // Career actions
  submit_application: { label: 'Application Submitted', color: 'bg-green-100 text-green-800', icon: FileText },
  
  default: { label: 'Action', color: 'bg-neutral-100 text-[#1a1a2e]', icon: Activity }
};

// Entity type labels
const entityTypeLabels = {
  employee: 'Employee',
  client: 'Client',
  task: 'Task',
  department: 'Department',
  user: 'User',
  ticket: 'Ticket',
  job_application: 'Job Application',
  applicant: 'Applicant',
  admin: 'Admin',
  default: 'Entity'
};

// Portal labels and colors
const portalConfig = {
  admin: { label: 'Admin Portal', color: 'bg-pink-100 text-pink-800' },
  employee: { label: 'Employee Portal', color: 'bg-yellow-100 text-yellow-800' },
  client: { label: 'Client Portal', color: 'bg-blue-100 text-blue-800' },
  career: { label: 'Career Portal', color: 'bg-purple-100 text-purple-800' },
  default: { label: 'System', color: 'bg-neutral-100 text-[#1a1a2e]' }
};

// Multi-Select Filter Dropdown
function FilterDropdown({ label, options, selected, onChange, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 text-left border border-white/40 rounded-xl bg-white text-sm flex items-center justify-between hover:border-pink-300 transition-colors ${isOpen ? 'ring-2 ring-pink-500 border-pink-500' : ''}`}
      >
        <span className={selected.length > 0 ? 'text-[#1a1a2e]' : 'text-neutral-400'}>
          {selected.length > 0 ? `${selected.length} selected` : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 w-full mt-1 glass-card rounded-xl shadow-lg max-h-60 overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange([]); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-neutral-500 hover:bg-white/60 border-b"
            >
              Clear all
            </button>
            {options.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  if (selected.includes(option.value)) {
                    onChange(selected.filter(v => v !== option.value));
                  } else {
                    onChange([...selected, option.value]);
                  }
                }}
                className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-pink-50"
              >
                <span className={`w-4 h-4 border rounded flex items-center justify-center ${selected.includes(option.value) ? 'bg-pink-500 border-pink-500' : 'border-white/40'}`}>
                  {selected.includes(option.value) && <Check className="w-3 h-3 text-white" />}
                </span>
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, icon: Icon, trend, color = 'pink' }) {
  const colorClasses = {
    pink: 'from-[#FF3366] to-[#FF3366]',
    blue: 'from-[#3366FF] to-[#3366FF]',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600'
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-start justify-between mb-4">
        <p className="text-sm text-neutral-600">{title}</p>
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <p className="text-2xl font-bold text-[#1a1a2e]">{value}</p>
      {trend && (
        <p className="text-xs text-neutral-500 mt-2 flex items-center gap-1">
          <TrendingUp className="w-3 h-3" />
          {trend}
        </p>
      )}
    </div>
  );
}

export default function AdminAuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalLogs, setTotalLogs] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    actions: [],
    entityTypes: [],
    userTypes: [],
    portals: [],
    startDate: '',
    endDate: '',
    search: ''
  });
  
  // Available filter options (populated from stats)
  const [filterOptions, setFilterOptions] = useState({
    actions: [],
    entityTypes: [],
    portals: [
      { value: 'admin', label: 'Admin Portal' },
      { value: 'employee', label: 'Employee Portal' },
      { value: 'client', label: 'Client Portal' },
      { value: 'career', label: 'Career Portal' }
    ]
  });

  const ITEMS_PER_PAGE = 20;

  const fetchStats = async () => {
    try {
      const response = await adminApi.get('/api/admin/audit-logs/stats');
      setStats(response.data);
      
      // Set filter options from stats
      if (response.data.unique_actions) {
        setFilterOptions(prev => ({
          ...prev,
          actions: response.data.unique_actions.map(a => ({
            value: a,
            label: actionConfig[a]?.label || a.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
          }))
        }));
      }
      if (response.data.unique_entity_types) {
        setFilterOptions(prev => ({
          ...prev,
          entityTypes: response.data.unique_entity_types.map(e => ({
            value: e,
            label: entityTypeLabels[e] || e.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
          }))
        }));
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', ITEMS_PER_PAGE);
      params.append('skip', page * ITEMS_PER_PAGE);
      
      if (filters.actions.length === 1) {
        params.append('action', filters.actions[0]);
      }
      if (filters.entityTypes.length === 1) {
        params.append('entity_type', filters.entityTypes[0]);
      }
      if (filters.userTypes.length === 1) {
        params.append('user_type', filters.userTypes[0]);
      }
      if (filters.portals.length === 1) {
        params.append('portal', filters.portals[0]);
      }
      if (filters.startDate) {
        params.append('start_date', new Date(filters.startDate).toISOString());
      }
      if (filters.endDate) {
        params.append('end_date', new Date(filters.endDate).toISOString());
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      
      const response = await adminApi.get(`/api/admin/audit-logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setTotalLogs(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load audit logs');
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleRefresh = () => {
    fetchStats();
    fetchLogs();
    toast.success('Audit logs refreshed');
  };

  const clearFilters = () => {
    setFilters({
      actions: [],
      entityTypes: [],
      userTypes: [],
      portals: [],
      startDate: '',
      endDate: '',
      search: ''
    });
    setPage(0);
  };

  const hasActiveFilters = filters.actions.length > 0 || filters.entityTypes.length > 0 || 
                           filters.userTypes.length > 0 || filters.portals.length > 0 || 
                           filters.startDate || filters.endDate || filters.search;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Parse as UTC if no timezone info (backend stores in UTC)
    let date;
    if (typeof timestamp === 'string') {
      // If timestamp doesn't end with Z or timezone offset, append Z to treat as UTC
      if (!timestamp.endsWith('Z') && !timestamp.match(/[+-]\d{2}:\d{2}$/)) {
        date = new Date(timestamp + 'Z');
      } else {
        date = new Date(timestamp);
      }
    } else {
      date = new Date(timestamp);
    }
    
    // Format in user's local timezone (India Standard Time for en-IN locale)
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Asia/Kolkata'  // Explicitly use IST for consistency
    }).format(date);
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    // Parse as UTC if no timezone info
    let date;
    if (typeof timestamp === 'string') {
      if (!timestamp.endsWith('Z') && !timestamp.match(/[+-]\d{2}:\d{2}$/)) {
        date = new Date(timestamp + 'Z');
      } else {
        date = new Date(timestamp);
      }
    } else {
      date = new Date(timestamp);
    }
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatTimestamp(timestamp);
  };

  const getActionConfig = (action) => {
    return actionConfig[action] || actionConfig.default;
  };

  const totalPages = Math.ceil(totalLogs / ITEMS_PER_PAGE);

  // Filter logs locally if multiple filters selected
  const filteredLogs = logs.filter(log => {
    if (filters.actions.length > 1 && !filters.actions.includes(log.action)) return false;
    if (filters.entityTypes.length > 1 && !filters.entityTypes.includes(log.entity_type)) return false;
    return true;
  });

  return (
    <div className="p-8" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a2e]">Audit Logs</h1>
          <p className="text-neutral-600 mt-1">Track and monitor all system activities</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            className="px-4 py-2 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60 flex items-center gap-2"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Logs"
            value={stats.total_logs?.toLocaleString() || 0}
            icon={FileText}
            color="pink"
          />
          <StatCard
            title="Last 24 Hours"
            value={stats.recent_24h?.toLocaleString() || 0}
            icon={Clock}
            trend="Recent activity"
            color="blue"
          />
          <StatCard
            title="Action Types"
            value={stats.unique_actions?.length || 0}
            icon={Activity}
            color="green"
          />
          <StatCard
            title="Entity Types"
            value={stats.unique_entity_types?.length || 0}
            icon={Building2}
            color="purple"
          />
        </div>
      )}

      {/* Filters Section */}
      <div className="glass-card mb-6">
        <div className="p-4 border-b border-white/30 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-colors ${showFilters ? 'bg-pink-50 border-pink-200 text-pink-700' : 'border-white/40 text-neutral-700 hover:bg-white/60'}`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-pink-500"></span>
              )}
            </button>
            
            {/* Quick Search */}
            <div className="relative">
              <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search logs..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 pr-4 py-2 border border-white/40 rounded-xl text-sm w-64 focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                data-testid="search-logs-input"
              />
            </div>
          </div>
          
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-neutral-500 hover:text-pink-600 flex items-center gap-1"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4" />
              Clear all filters
            </button>
          )}
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="p-4 bg-white/40 grid grid-cols-6 gap-4">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Actions</label>
              <FilterDropdown
                label="Actions"
                options={filterOptions.actions}
                selected={filters.actions}
                onChange={(val) => setFilters(prev => ({ ...prev, actions: val }))}
                placeholder="All actions"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Entity Type</label>
              <FilterDropdown
                label="Entity Type"
                options={filterOptions.entityTypes}
                selected={filters.entityTypes}
                onChange={(val) => setFilters(prev => ({ ...prev, entityTypes: val }))}
                placeholder="All types"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Portal</label>
              <FilterDropdown
                label="Portal"
                options={filterOptions.portals}
                selected={filters.portals}
                onChange={(val) => setFilters(prev => ({ ...prev, portals: val }))}
                placeholder="All portals"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">User Type</label>
              <FilterDropdown
                label="User Type"
                options={[
                  { value: 'admin', label: 'Admin' },
                  { value: 'employee', label: 'Employee' },
                  { value: 'client', label: 'Client' },
                  { value: 'applicant', label: 'Applicant' }
                ]}
                selected={filters.userTypes}
                onChange={(val) => setFilters(prev => ({ ...prev, userTypes: val }))}
                placeholder="All users"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">From Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-brutal-pink/30"
                data-testid="start-date-filter"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">To Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-brutal-pink/30"
                data-testid="end-date-filter"
              />
            </div>
          </div>
        )}
      </div>

      {/* Logs Table */}
      <div className="soft-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="audit-logs-table">
            <thead className="bg-white/40 border-b border-white/40">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Timestamp</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Action</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Portal</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Entity</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Performed By</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/30">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <RefreshCw className="w-5 h-5 text-pink-500 animate-spin" />
                      <span className="text-neutral-500">Loading audit logs...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <FileText className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                    <p className="text-neutral-500">No audit logs found</p>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="mt-2 text-sm text-pink-600 hover:underline"
                      >
                        Clear filters to see all logs
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => {
                  const config = getActionConfig(log.action);
                  const ActionIcon = config.icon;
                  const portal = portalConfig[log.portal] || portalConfig.default;
                  
                  return (
                    <tr key={log._id} className="hover:bg-white/60 transition-colors" data-testid={`log-row-${log._id}`}>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-[#1a1a2e]">{formatRelativeTime(log.timestamp)}</span>
                          <span className="text-xs text-neutral-500">{formatTimestamp(log.timestamp)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
                          <ActionIcon className="w-3 h-3" />
                          {config.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${portal.color}`}>
                          {portal.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-[#1a1a2e] capitalize">
                            {entityTypeLabels[log.entity_type] || log.entity_type || 'N/A'}
                          </span>
                          <span className="text-xs text-neutral-500 font-mono truncate max-w-[200px]" title={log.entity_id}>
                            {log.entity_id ? `ID: ${log.entity_id.slice(-8)}` : '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-neutral-500" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-[#1a1a2e]">{log.actor_name || log.user_name || 'Unknown'}</span>
                            <span className="text-xs text-neutral-500">{log.actor_email || log.user_email || (log.actor_type || log.user_type || '-')}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <div className="text-xs text-neutral-600 space-y-0.5">
                            {Object.entries(log.details).slice(0, 2).map(([key, value]) => (
                              <div key={key} className="flex items-center gap-1">
                                <span className="text-neutral-400">{key.replace(/_/g, ' ')}:</span>
                                <span className="font-medium truncate max-w-[150px]" title={String(value)}>
                                  {String(value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-neutral-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalLogs > ITEMS_PER_PAGE && (
          <div className="px-6 py-4 border-t border-white/30 flex items-center justify-between bg-white/40">
            <p className="text-sm text-neutral-600">
              Showing {page * ITEMS_PER_PAGE + 1} to {Math.min((page + 1) * ITEMS_PER_PAGE, totalLogs)} of {totalLogs} logs
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(prev => Math.max(0, prev - 1))}
                disabled={page === 0}
                className="p-2 rounded-xl border border-white/40 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white"
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-neutral-600 px-3">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage(prev => Math.min(totalPages - 1, prev + 1))}
                disabled={page >= totalPages - 1}
                className="p-2 rounded-xl border border-white/40 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white"
                data-testid="next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
